import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const severityMeta = {
  Good: {
    accent: 'from-emerald-400/80 to-emerald-500/80',
    description: 'Use for informative notifications that confirm healthy operating ranges.',
  },
  Warning: {
    accent: 'from-amber-400/80 to-orange-500/80',
    description: 'Ideal for drawing attention to values that need review soon.',
  },
  Failing: {
    accent: 'from-rose-500/80 to-red-600/80',
    description: 'Escalate when thresholds indicate critical issues that need immediate action.',
  },
};

const shimmerVariant = {
  animate: {
    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
    transition: { duration: 8, repeat: Infinity, ease: 'linear' },
  },
};

function NewAlert() {
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';

  const [devices, setDevices] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(true);
  const [form, setForm] = useState({
    alert_name: '',
    select_unit: '',
    trigger_condition: 'Above',
    threshold_x: '',
    threshold_y: '',
    threshold_z: '',
    severity: 'Good',
    assigned_users: [],
    description: '',
  });
  const [emailInput, setEmailInput] = useState('');
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await fetch(`/get_user_devices.php?username=${encodeURIComponent(user)}`);
        const data = await res.json();
        setDevices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setDevices([]);
      } finally {
        setIsLoadingUnits(false);
      }
    };

    fetchDevices();
  }, [user]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const availableUnits = useMemo(() => {
    const units = new Set();
    devices.forEach((device) => {
      if (device.unit) units.add(device.unit);
    });
    return Array.from(units);
  }, [devices]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addEmail = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const trimmed = emailInput.trim();
      if (!trimmed) return;
      if (form.assigned_users.includes(trimmed)) {
        setToast({ message: 'Recipient already added.', success: false });
        return;
      }
      setForm((prev) => ({
        ...prev,
        assigned_users: [...prev.assigned_users, trimmed],
      }));
      setEmailInput('');
    }
  };

  const removeEmail = (index) => {
    setForm((prev) => ({
      ...prev,
      assigned_users: prev.assigned_users.filter((_, idx) => idx !== index),
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const payload = {
      ...form,
      assigned_users: form.assigned_users.join(','),
      user,
      alert_active: 1,
    };

    try {
      const res = await fetch('/add_alert.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setToast({ message: 'Alert created successfully.', success: true });
        setForm({
          alert_name: '',
          select_unit: '',
          trigger_condition: 'Above',
          threshold_x: '',
          threshold_y: '',
          threshold_z: '',
          severity: 'Good',
          assigned_users: [],
          description: '',
        });
      } else {
        throw new Error(data.message || 'Failed to create alert');
      }
    } catch (error) {
      console.error(error);
      setToast({ message: 'Unable to create alert. Please try again.', success: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#151C2A] via-[#0C1017] to-[#080B10]" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.45, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="absolute left-6 top-16 h-72 w-72 rounded-full bg-primary/30 blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
          className="absolute bottom-10 right-8 h-80 w-80 rounded-full bg-rose-500/25 blur-3xl"
        />
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed right-6 top-6 z-50 flex items-center gap-3 rounded-full border px-5 py-3 text-sm shadow-xl ${
              toast.success
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
                : 'border-rose-500/60 bg-rose-500/15 text-rose-200'
            }`}
          >
            <span className="text-lg">{toast.success ? '✅' : '⚠️'}</span>
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 py-16"
      >
        <header className="flex flex-col gap-3">
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">Alerts</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-4xl font-semibold">Create proactive alerts</h1>
              <p className="max-w-xl text-sm text-white/65">
                Define trigger conditions, escalation severity and who should know the moment a threshold is crossed.
              </p>
            </div>
            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.3em] text-white/60">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400/70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-300" />
              </span>
              Instant notifications
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-4xl">
          <form onSubmit={submit} className="space-y-8">
            <section className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Alert details</h2>
                  <p className="text-sm text-white/60">Choose the unit to monitor and describe the objective of this alert.</p>
                </div>
                <motion.span
                  className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-sm font-semibold uppercase tracking-wide text-white/70 md:flex"
                  animate={{ rotate: [0, 6, -4, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  01
                </motion.span>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Alert name</span>
                  <input
                    type="text"
                    name="alert_name"
                    value={form.alert_name}
                    onChange={updateField}
                    required
                    placeholder="E.g. Pump vibration spike"
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none focus:ring-0"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Alert severity</span>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(severityMeta).map((level) => {
                      const active = form.severity === level;
                      return (
                        <button
                          key={level}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, severity: level }))}
                          className={`relative overflow-hidden rounded-full border px-4 py-2 text-sm font-semibold transition ${
                            active
                              ? 'border-transparent bg-gradient-to-r from-primary/80 to-rose-500/70 text-white shadow-lg shadow-primary/20'
                              : 'border-white/10 bg-white/5 text-white/70 hover:border-primary/40 hover:text-white'
                          }`}
                        >
                          <span className="relative z-10 flex items-center gap-2">
                            <span className="text-base">{level === 'Good' ? '🟢' : level === 'Warning' ? '🟠' : '🔴'}</span>
                            {level}
                          </span>
                          {active && (
                            <motion.span
                              layoutId="alertSeverityGlow"
                              className={`absolute inset-0 -z-0 bg-gradient-to-r ${severityMeta[level].accent} opacity-90`}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </label>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Select unit</span>
                  <select
                    name="select_unit"
                    value={form.select_unit}
                    onChange={updateField}
                    required
                    disabled={isLoadingUnits}
                    className="w-full appearance-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="">{isLoadingUnits ? 'Loading units…' : 'Choose a unit'}</option>
                    {availableUnits.map((unit) => (
                      <option key={unit} value={unit} className="bg-[#0C1017] text-white">
                        {unit}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Trigger condition</span>
                  <select
                    name="trigger_condition"
                    value={form.trigger_condition}
                    onChange={updateField}
                    className="w-full appearance-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="Above">Above threshold</option>
                    <option value="Below">Below threshold</option>
                  </select>
                </label>
              </div>

              <p className="mt-6 text-xs text-white/45">
                {severityMeta[form.severity].description}
              </p>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Thresholds & recipients</h2>
                  <p className="text-sm text-white/60">Define the limits that trigger this alert and who receives it.</p>
                </div>
                <motion.span
                  className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-sm font-semibold uppercase tracking-wide text-white/70 md:flex"
                  animate={{ rotate: [0, -4, 6, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
                >
                  02
                </motion.span>
              </div>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {['threshold_x', 'threshold_y', 'threshold_z'].map((axis) => (
                  <label key={axis} className="flex flex-col gap-2 text-sm">
                    <span className="text-white/70">
                      {axis.endsWith('x') ? 'Axis X' : axis.endsWith('y') ? 'Axis Y' : 'Axis Z'} threshold (mm/s)
                    </span>
                    <input
                      type="number"
                      name={axis}
                      value={form[axis]}
                      onChange={updateField}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                    />
                  </label>
                ))}
                <label className="flex flex-col gap-2 text-sm md:col-span-2">
                  <span className="text-white/70">Description</span>
                  <textarea
                    name="description"
                    rows={3}
                    value={form.description}
                    onChange={updateField}
                    placeholder="Add context so responders know exactly what to do."
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                  />
                </label>
              </div>

              <div className="mt-6">
                <div className="flex flex-wrap gap-2">
                  {form.assigned_users.map((email, idx) => (
                    <motion.span
                      key={email}
                      layout
                      className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs text-white/80 backdrop-blur"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail(idx)}
                        className="rounded-full bg-white/10 px-2 py-0.5 text-white/60 transition hover:bg-rose-500/40 hover:text-white"
                      >
                        ×
                      </button>
                    </motion.span>
                  ))}
                </div>
                <label className="mt-3 flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Notify</span>
                  <input
                    type="email"
                    placeholder="Press enter to add each recipient"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    onKeyDown={addEmail}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                  />
                </label>
              </div>
            </section>

            <div className="flex justify-end">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                className={`group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full px-7 py-3 text-sm font-semibold uppercase tracking-[0.3em] transition ${
                  isSubmitting
                    ? 'cursor-not-allowed border border-white/10 bg-white/10 text-white/60'
                    : 'border border-transparent bg-gradient-to-r from-[#bf1d2e] via-[#f05263] to-[#fb923c] text-white shadow-lg shadow-[#bf1d2e]/30'
                }`}
                aria-label="Create alert"
              >
                <span className="relative z-10">{isSubmitting ? 'Saving…' : 'Create alert'}</span>
                {!isSubmitting && (
                  <motion.span
                    className="relative z-10 flex h-2.5 w-2.5 items-center justify-center"
                    animate={{ opacity: [0.6, 1, 0.6], x: [0, 4, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-3 w-3 text-white"
                    >
                      <path d="M7.5 4.5l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </motion.span>
                )}
                <motion.span
                  variants={shimmerVariant}
                  animate="animate"
                  className="absolute inset-0 -z-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.18)_45%,rgba(255,255,255,0.08)_100%)] bg-[length:300%_100%] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

export default NewAlert;
